// Shared BOQ calculation + helpers used by BOTH the project BOQ/Estimate editor
// and the BOQ Template Builder so the two are functionally identical.

export const unitOptions = ['Sq.ft', 'Rft.', 'No.s', 'R.Mtr', 'Mtr', 'Cu.ft', 'Lot', 'LS', 'Kg', 'Cum', 'Sqm', 'Day'];

export const defaultRoomNames = [
  'Living Room', 'Kitchen', 'Master Bedroom', 'Bedroom 2', 'Bedroom 3',
  'Bathroom 1', 'Bathroom 2', 'Dining', 'Foyer', 'Balcony', 'Utility', 'Study',
  'Mandir', 'Home Theater', 'Reception', 'Workstations', 'Conference Room', 'Pantry'
];

export function genId() {
  return crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function emptyRow(sno) {
  return {
    id: genId(), sno, description: '', specification: '',
    dnb_item_id: '', dnb_variant_id: '',
    l: 0, w: 0, h: 0, nos: 1, qty: 0, unit: 'Sq.ft', rate: 0, amount: 0, area_basis: 'auto'
  };
}

export function formatINR(n) {
  if (!n && n !== 0) return '0';
  return Number(n).toLocaleString('en-IN');
}

/**
 * Quantity auto-calculation.
 * Never silently includes a blank dimension as 0 in a multiplication.
 *  - L × W           = area (sq ft)  [floor / ceiling / flat surfaces]
 *  - L × H           = area (sq ft)  [walls / partitions / vertical surfaces]
 *  - L × W × H       = volume (cu ft) [volumetric items]
 *  - L only          = length (running)
 *  - none            = count (Nos multiplier)
 * area_basis ('lw' | 'lh' | 'lwh' | 'auto') lets the user override when ambiguous.
 * Legacy values 'floor' -> 'lw', 'wall' -> 'lh' are migrated automatically.
 */
export function calcRow(row) {
  const l = Number(row.l) || 0;
  const w = Number(row.w) || 0;
  const h = Number(row.h) || 0;
  const nos = Number(row.nos) || 1;

  let basis = row.area_basis || 'auto';
  if (basis === 'floor') basis = 'lw';
  if (basis === 'wall') basis = 'lh';
  // Drop an explicit basis if its required dimensions aren't present
  if (basis === 'lw' && !(l > 0 && w > 0)) basis = 'auto';
  if (basis === 'lh' && !(l > 0 && h > 0)) basis = 'auto';
  if (basis === 'lwh' && !(l > 0 && w > 0 && h > 0)) basis = 'auto';

  let qty = 0;
  let usedBasis = null;   // 'lw' | 'lh' | 'lwh' | 'l' | 'nos'
  let dimType = null;     // 'area' | 'volume' | 'length' | 'count'

  if (basis === 'lw') { qty = l * w * nos; usedBasis = 'lw'; dimType = 'area'; }
  else if (basis === 'lh') { qty = l * h * nos; usedBasis = 'lh'; dimType = 'area'; }
  else if (basis === 'lwh') { qty = l * w * h * nos; usedBasis = 'lwh'; dimType = 'volume'; }
  else {
    // auto
    if (l > 0 && w > 0 && h > 0) { qty = l * w * h * nos; usedBasis = 'lwh'; dimType = 'volume'; }
    else if (l > 0 && w > 0) { qty = l * w * nos; usedBasis = 'lw'; dimType = 'area'; }
    else if (l > 0 && h > 0) { qty = l * h * nos; usedBasis = 'lh'; dimType = 'area'; }
    else if (l > 0) { qty = l * nos; usedBasis = 'l'; dimType = 'length'; }
    else { qty = nos; usedBasis = 'nos'; dimType = 'count'; }
  }

  qty = Math.round(qty * 100) / 100;
  const rate = Number(row.rate) || 0;
  const amount = Math.round(qty * rate);
  return { ...row, qty, amount, _basis: usedBasis, _dimType: dimType };
}

export function dimUnitLabel(dimType) {
  if (dimType === 'volume') return 'cu ft';
  if (dimType === 'area') return 'sq ft';
  return null;
}

export function getRoomTotal(room) {
  return (room?.items || []).reduce((s, i) => s + (i.amount || 0), 0);
}
